import { HexColorPicker } from "react-colorful";
import { Box } from "@mui/material";
import { Layers, Map as MapIcon, Flight, CellTower } from "@mui/icons-material";

import { hexToRgbString, rgbToHex } from "../../utils/mapUtils";
import { SegmentedSetting, SidebarPanel, SidebarSection, SliderSetting, SwatchSetting, SwitchSetting, accentOf } from "./SidebarKit";
import { MAP_TYPES } from "./BaseMapLayer";

const pct = (v) => `${Math.round(v * 100)}%`;

const rgbStringToHex = (value) => {
  const [r, g, b] = String(value).split(",").map((v) => parseInt(v, 10) || 0);
  return rgbToHex(r, g, b);
};

// Color row: swatch opens a picker in a pop-up next to the sidebar
const ColorSetting = ({ label, value, onChange, accent }) => (
  <SwatchSetting
    label={label}
    sub={`rgb(${value})`}
    accent={accent}
    popupTitle={label.toUpperCase()}
    swatch={<Box sx={{ width: 34, height: 20, backgroundColor: `rgb(${value})` }} />}
  >
    <HexColorPicker color={rgbStringToHex(value)} onChange={(hex) => onChange(hexToRgbString(hex))} />
  </SwatchSetting>
);

const MapSettingsSidebar = ({
  mapType,              // 'roadmap' | 'satellite' | 'osm' (with setMapType)
  setMapType,
  showGoogleMap,        // older on/off form, used when setMapType isn't passed
  setShowGoogleMap,
  showBorder,
  setShowBorder,
  showAirports,
  setShowAirports,
  showOutposts,
  setShowOutposts,
  outpostOpacity,
  setOutpostOpacity,
  overlayOpacity,
  setOverlayOpacity,
  showNoFlyRegion,
  setShowNoFlyRegion,
  noFlyRegionColor,
  setNoFlyRegionColor,
  noFlyRegionFillColor,
  setNoFlyRegionFillColor,
  airportOpacity,
  setAirportOpacity,
  mode = "live",        // 'live' (Radar) | 'replay' (Review)
}) => {
  const accent = accentOf(mode);

  return (
    <SidebarPanel accent={accent}>
      <SidebarSection icon={MapIcon} title="Base map" accent={accent}>
        {setMapType ? (
          // Radar / Review: Google road map, Google satellite or OpenStreetMap
          <SegmentedSetting
            label="Map type"
            value={mapType}
            onChange={setMapType}
            accent={accent}
            options={Object.entries(MAP_TYPES).map(([value, t]) => ({ value, label: t.label.toUpperCase() }))}
          />
        ) : (
          // Pages that still pass showGoogleMap (Region): Google road map or OpenStreetMap
          <SwitchSetting
            label="Google Maps"
            hint={showGoogleMap ? "Google road map" : "OpenStreetMap"}
            checked={showGoogleMap}
            onChange={setShowGoogleMap}
            accent={accent}
          />
        )}
        <SwitchSetting label="Borders" checked={showBorder} onChange={setShowBorder} accent={accent} />
        <SliderSetting
          label="Dark overlay"
          value={overlayOpacity}
          onChange={setOverlayOpacity}
          min={0}
          max={1}
          step={0.01}
          format={pct}
          accent={accent}
        />
      </SidebarSection>

      <SidebarSection icon={Flight} title="Airports" accent={accent}>
        <SwitchSetting label="Show airports" checked={showAirports} onChange={setShowAirports} accent={accent} />
        <SliderSetting
          label="Opacity"
          value={airportOpacity}
          onChange={setAirportOpacity}
          min={0}
          max={1}
          step={0.01}
          format={pct}
          accent={accent}
          disabled={!showAirports}
        />
      </SidebarSection>

      <SidebarSection icon={CellTower} title="Outposts" accent={accent}>
        <SwitchSetting label="Show outposts" checked={showOutposts} onChange={setShowOutposts} accent={accent} />
        <SliderSetting
          label="Opacity"
          value={outpostOpacity}
          onChange={setOutpostOpacity}
          min={0}
          max={1}
          step={0.01}
          format={pct}
          accent={accent}
          disabled={!showOutposts}
        />
      </SidebarSection>

      <SidebarSection icon={Layers} title="Regions" accent={accent}>
        <SwitchSetting label="Show regions" checked={showNoFlyRegion} onChange={setShowNoFlyRegion} accent={accent} />
        <Box sx={{ opacity: showNoFlyRegion ? 1 : 0.45 }}>
          <ColorSetting label="Border color" value={noFlyRegionColor} onChange={setNoFlyRegionColor} accent={accent} />
          <ColorSetting label="Fill color" value={noFlyRegionFillColor} onChange={setNoFlyRegionFillColor} accent={accent} />
        </Box>
      </SidebarSection>
    </SidebarPanel>
  );
};

export default MapSettingsSidebar;
